import { useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Board, Button, EmptyState, ErrorBanner, Field, Money, PageHeader, ResourceFormScreen, Select, Skeleton, useAction, useApiQuery, useInvalidateResource, useMeta, useResourceList, type BoardColumn, type FieldMeta } from '../../engine';
import { formatDate, formatMoney } from '../../lib/format';
import type { StageRow } from './OpportunityHeader';

// The pipeline board: one column per stage of the chosen pipeline (all stages, even empty),
// Σ one-time + recurring per column from GET /v1/opportunities/pipeline-summary, and a drag
// between columns that calls POST /v1/opportunities/:id/move-stage (never a raw PATCH — the
// action writes the history note, re-applies the stage probability and wins/loses on the
// closing stages). Built on the engine's Board view + hooks; the screen is `kind: 'board'`.

interface StageSummary {
  stage_id: string;
  name: string;
  count: string;
  open_count: string;
  one_time_minor: string;
  recurring_minor: string;
}

interface OpportunityRow extends Record<string, unknown> {
  id: string;
  name: string;
  ref: string | null;
  stage_id: string;
  status: string;
  probability: number | null;
  one_time_minor: string;
  recurring_minor: string;
  recurring_interval: string | null;
  currency: string;
  expected_close: string | null;
  client_ref?: { id: string; label: string | null } | null;
  owner_tech_ref?: { id: string; label: string | null } | null;
}

const STAGE_FIELD: FieldMeta = { name: 'stage_id', label: 'Stage', type: 'relation', required: true, readOnly: false, nullable: false, filterable: true, sortable: true, searchable: false, inCreate: true, inUpdate: true, inList: true, inDetail: true, relation: { resource: 'pipeline-stages', displayField: 'name' } };

export function OpportunityCard({ row }: { row: OpportunityRow }): ReactNode {
  return (
    <div className="hf-stack" style={{ gap: 4 }}>
      <div className="hf-between" style={{ fontSize: 'var(--t-12)' }}>
        <span className="hf-muted">{row.client_ref?.label ?? '—'}</span>
        {row.ref && <span className="hf-mono hf-muted">{row.ref}</span>}
      </div>
      <div className="hf-between hf-num" style={{ fontSize: 'var(--t-12)' }}>
        <span>
          <Money minor={row.one_time_minor} currency={row.currency} />
          {row.recurring_minor !== '0' && (
            <span className="hf-muted">
              {' '}
              + <Money minor={row.recurring_minor} currency={row.currency} />
              {row.recurring_interval ? `/${row.recurring_interval === 'monthly' ? 'mo' : row.recurring_interval === 'annual' ? 'yr' : row.recurring_interval === 'quarterly' ? 'qtr' : '6mo'}` : ''}
            </span>
          )}
        </span>
        <span className="hf-muted">{row.probability == null ? '' : `${row.probability}%`}</span>
      </div>
      <div className="hf-between hf-muted" style={{ fontSize: 'var(--t-11)' }}>
        <span>{row.owner_tech_ref?.label ?? 'Unassigned'}</span>
        <span>{row.expected_close ? formatDate(row.expected_close) : ''}</span>
      </div>
    </div>
  );
}

export function OpportunityBoard() {
  const navigate = useNavigate();
  const meta = useMeta('opportunities');
  const pipelines = useResourceList<{ id: string; name: string; is_default: boolean }>('pipelines', { sort: 'name', limit: 50 }, { staleTime: 60_000 });
  const [chosen, setChosen] = useState<string | null>(null);
  const pipelineId = chosen ?? pipelines.rows.find((p) => p.is_default)?.id ?? pipelines.rows[0]?.id ?? null;
  const stages = useResourceList<StageRow>('pipeline-stages', { filter: [{ field: 'pipeline_id', op: 'eq', value: pipelineId ?? '' }], sort: 'sort_order', limit: 50 }, { enabled: Boolean(pipelineId), staleTime: 60_000 });
  const list = useResourceList<OpportunityRow>('opportunities', { filter: [{ field: 'pipeline_id', op: 'eq', value: pipelineId ?? '' }, { field: 'status', op: 'ne', value: 'inactive' }], expand: ['client', 'owner_tech'], sort: '-created_at' }, { enabled: Boolean(pipelineId), limit: 200, keepPrevious: true });
  const summary = useApiQuery<{ pipeline_id: string; stages: StageSummary[] }>(['opportunities', 'pipeline-summary', pipelineId], '/v1/opportunities/pipeline-summary', { enabled: Boolean(pipelineId), query: { pipeline_id: pipelineId } });
  const invalidate = useInvalidateResource();
  const move = useAction('opportunities', 'move-stage', { toast: { success: 'Stage updated' }, onSuccess: () => { invalidate('opportunities'); void summary.refetch(); } });
  const [newOpen, setNewOpen] = useState(false);

  const columns = useMemo<BoardColumn[]>(() => {
    const sums = new Map((summary.data?.stages ?? []).map((s) => [s.stage_id, s]));
    const currency = list.rows[0]?.currency ?? 'USD';
    return stages.rows.map((s) => {
      const sum = sums.get(s.id);
      const total = sum ? formatMoney(sum.one_time_minor, currency) : null;
      return { value: s.id, label: total ? `${s.name} · ${total}` : s.name, tone: s.is_won ? 'pos' : s.is_lost ? 'neg' : 'muted' };
    });
  }, [stages.rows, summary.data, list.rows]);

  if (meta.error) return <ErrorBanner error={meta.error} />;
  if (!meta.data || pipelines.isLoading) return <Skeleton lines={6} />;

  return (
    <div className="hf-page">
      <PageHeader
        title="Pipeline"
        eyebrow="crm"
        subtitle="Drag a deal between stages; the closing stages mark it won or lost."
        actions={
          <>
            <Button variant="ghost" icon="table" onClick={() => navigate('/opportunities')}>
              List
            </Button>
            <Button variant="primary" icon="plus" onClick={() => setNewOpen(true)}>
              New opportunity
            </Button>
          </>
        }
      >
        {pipelines.rows.length > 1 && (
          <Field label="Pipeline" inline>
            <Select size="sm" aria-label="Pipeline" value={pipelineId ?? ''} options={pipelines.rows.map((p) => ({ value: p.id, label: p.name }))} onChange={(e) => setChosen(e.target.value)} />
          </Field>
        )}
      </PageHeader>
      {list.error && <ErrorBanner error={list.error} />}
      {pipelineId ? (
        <Board<OpportunityRow>
          rows={list.rows}
          keyOf={(r) => r.id}
          field={STAGE_FIELD}
          columns={columns}
          labelOf={(r) => r.name}
          card={(r) => <OpportunityCard row={r} />}
          onOpen={(r) => navigate(`/opportunities/${r.id}`)}
          onMove={(r, stageId) => move.mutate({ id: r.id, body: { stage_id: stageId } })}
          empty={<EmptyState title="No open opportunities" hint="Create the first deal to start the pipeline." icon="target" action={<Button variant="primary" icon="plus" onClick={() => setNewOpen(true)}>New opportunity</Button>} />}
        />
      ) : (
        <EmptyState title="No pipeline" hint="Define a pipeline and its stages first." icon="workflow" />
      )}
      {newOpen && <ResourceFormScreen resource="opportunities" onClose={() => setNewOpen(false)} onDone={(row) => { setNewOpen(false); invalidate('opportunities'); void summary.refetch(); navigate(`/opportunities/${String(row.id)}`); }} />}
    </div>
  );
}
